import * as Location from 'expo-location';
import { Platform } from 'react-native';

/**
 * Cross-platform "get a ZIP from the device's location" helper.
 *
 * Native (iOS / Android): expo-location handles permission + reverse-geocode.
 * Web: expo-location's reverse-geocoder requires a Google Maps API key, so we
 * fall back to the browser's geolocation API plus OpenStreetMap's Nominatim
 * reverse endpoint, which is free and key-less.
 *
 * Nominatim's terms ask for a User-Agent and rate-limit polite usage (≤1
 * req/s). We're well under that for onboarding. Before scaling we should
 * either run our own Nominatim or swap to a paid service.
 *
 * Returns the postal code string (already cleaned to the first 5 digits for
 * US ZIPs) or null if no postcode could be resolved.
 */
export async function fetchPostalCodeFromGps(): Promise<string | null> {
  if (Platform.OS === 'web') {
    return await webGpsLookup();
  }
  return await nativeGpsLookup();
}

async function nativeGpsLookup(): Promise<string | null> {
  const { status } = await Location.requestForegroundPermissionsAsync();
  if (status !== 'granted') {
    throw new Error('Location permission denied');
  }
  const pos = await Location.getCurrentPositionAsync({});
  // Play Services' geocoder can fail with an opaque gRPC
  // DEADLINE_EXCEEDED when its backend is unreachable (VPNs and
  // filtered networks trigger this — Wave 57, real-device report).
  // We already have the coordinates, so fall back to the same
  // Nominatim reverse lookup the web build uses.
  try {
    const addresses = await Location.reverseGeocodeAsync({
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
    });
    const found = addresses.find((a) => a.postalCode)?.postalCode;
    if (found) return cleanZip(found);
  } catch (err) {
    console.warn('native reverse geocode failed, trying Nominatim', err);
  }
  return await nominatimZip(pos.coords.latitude, pos.coords.longitude);
}

async function webGpsLookup(): Promise<string | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new Error('Browser geolocation is not available');
  }
  const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, (err) =>
      reject(new Error(err.message || 'Browser geolocation failed'))
    , { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 });
  });

  return await nominatimZip(pos.coords.latitude, pos.coords.longitude);
}

/** Key-less reverse geocode via OpenStreetMap Nominatim — shared by the
 *  web path and the native fallback. */
async function nominatimZip(lat: number, lon: number): Promise<string | null> {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`;
  const resp = await fetch(url, {
    headers: {
      Accept: 'application/json',
      // Nominatim asks for a descriptive User-Agent; browsers don't let us set
      // it, so we identify via the Referer that fetch sends automatically.
    },
  });
  if (!resp.ok) {
    throw new Error(`Reverse-geocode failed (${resp.status})`);
  }
  const data: { address?: { postcode?: string } } = await resp.json();
  const postcode = data?.address?.postcode;
  return postcode ? cleanZip(postcode) : null;
}

function cleanZip(raw: string): string {
  // US ZIPs are "12345" or "12345-6789"; non-US can be alphanumeric. We trim
  // to the first 5 digits (good for the US onboarding stub) and let the
  // climate-zone lookup decide what to do with non-US input.
  const digits = raw.replace(/[^0-9]/g, '');
  return digits.slice(0, 5);
}
